import type { Grid, GridNode, PathFindingFunction, SVGDrawFunction, XYPosition } from '@tisoap/react-flow-smart-edge';

const manhattan = (dx: number, dy: number): number => dx + dy;

const reconstructPath = (endNode: GridNode): number[][] => {
  const path: number[][] = [];
  let node: GridNode | undefined = endNode;
  while (node) {
    path.push([node.x, node.y]);
    node = node.parent;
  }
  return path.reverse();
};

const selectNodeWithLowestEstimatedTotalCost = (openList: GridNode[]): GridNode => {
  let bestIdx = 0;
  for (let i = 1; i < openList.length; i++) {
    const iCost = openList[i].estimatedTotalCost ?? Infinity;
    const bestCost = openList[bestIdx].estimatedTotalCost ?? Infinity;
    if (iCost < bestCost) {
      bestIdx = i;
    }
  }
  return openList.splice(bestIdx, 1)[0];
};

const processNeighbor = (
  neighbor: GridNode,
  current: GridNode,
  end: GridNode,
  openList: GridNode[],
  weight: number
): void => {
  if (neighbor.closed) return;

  const tentativeG = (current.costFromStart ?? 0) + 1;

  if (!neighbor.opened || tentativeG < (neighbor.costFromStart ?? Infinity)) {
    neighbor.costFromStart = tentativeG;
    neighbor.heuristicCostToGoal =
      neighbor.heuristicCostToGoal ?? weight * manhattan(Math.abs(neighbor.x - end.x), Math.abs(neighbor.y - end.y));
    neighbor.estimatedTotalCost = (neighbor.costFromStart ?? 0) + (neighbor.heuristicCostToGoal ?? 0);
    neighbor.parent = current;

    if (!neighbor.opened) {
      neighbor.opened = true;
      openList.push(neighbor);
    }
  }
};

export const pathfindingAStarNoDiagonal: PathFindingFunction = (
  grid: Grid,
  start: XYPosition,
  end: XYPosition
): number[][] => {
  const startNode = grid.getNodeAt(start.x, start.y);
  const endNode = grid.getNodeAt(end.x, end.y);

  const openList: GridNode[] = [];

  startNode.costFromStart = 0;
  startNode.heuristicCostToGoal = 0;
  startNode.estimatedTotalCost = 0;
  startNode.opened = true;
  openList.push(startNode);

  while (openList.length > 0) {
    const node = selectNodeWithLowestEstimatedTotalCost(openList);
    node.closed = true;

    if (node === endNode) {
      return reconstructPath(endNode);
    }

    const neighbors = grid.getNeighbors(node, 'Never');

    for (const neighbor of neighbors) {
      processNeighbor(neighbor, node, endNode, openList, 1);
    }
  }

  throw new Error('No path found');
};

export const svgDrawStraightLinePath: SVGDrawFunction = (
  source: XYPosition,
  target: XYPosition,
  path: number[][]
): string => {
  let svgPathString = `M ${String(source.x)}, ${String(source.y)} `;
  path.forEach((point) => {
    const [x, y] = point;
    svgPathString += `L ${String(x)}, ${String(y)} `;
  });
  svgPathString += `L ${String(target.x)}, ${String(target.y)} `;
  return svgPathString;
};
